import React, { useRef, useEffect } from "react";
import styled from "styled-components";

import { DndContext, closestCenter, PointerSensor, TouchSensor, useDndContext, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import useDragAndDrop from "../../Model/Share/Normal/useDragAndDrop";
import { useLanguage } from '../../Context/LanguageContext';
import { normalizeCenterType } from '../../Utils/centerTypeUtils';

import UserProfile from "../../Component/Normal/UserProfile";
import CenterLogoImage from "./CenterLogoImage";
import CenterTypeSelect from "../../Component/MySettingPageComponent/CenterTypeSelect";
import LabeledDashedInput from "../../Component/MySettingPageComponent/LabeledDashedInput";
import CenterIntroTextArea from "../../Component/MySettingPageComponent/CenterIntroTextArea";
import LogoView from "../../Component/MySettingPageComponent/LogoView";

import DefaultLogoPath from '../../image/DefaultLogo.svg';
import { ReactComponent as PencilIcon } from '../../image/WhitePencel.svg';
import { ReactComponent as PlusIcon } from '../../image/Plus.svg';

const IMAGE_SIZE = {
  LOGO: 600,
  CENTER: 800
};

const ALLOWED_IMAGE_TYPES = ['jpg', 'png', 'jpeg'];
const MAX_CENTER_PHOTOS = 8;

const isValidImageType = (filetype) => ALLOWED_IMAGE_TYPES.includes(filetype.toLowerCase());

const parseProfiles = (profiles) => {
  if (Array.isArray(profiles)) return profiles;
  if (!profiles) return [];

  return profiles.split(',').filter(p => p !== '');
};

const calculateResizedimensions = (naturalWidth, naturalHeight, standardSize) => {
  let resizeWidth = 0;
  let resizeHeight = 0;

  if (naturalWidth < naturalHeight) {
    resizeWidth = standardSize;
    resizeHeight = (standardSize * naturalHeight) / naturalWidth;
  }
  else if (naturalWidth > naturalHeight) {
    resizeHeight = standardSize;
    resizeWidth = (standardSize * naturalWidth) / naturalHeight;
  }
  else {
    resizeWidth = standardSize;
    resizeHeight = standardSize;
  }

  return { resizeWidth, resizeHeight };
};

// 드래그 가능한 사진 래퍼 //
function SortablePictureCell(props) {
  const { id, isClicked, data, index, onClickDeleteButton } = props;

  const { active } = useDndContext();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: active ? transition : 'none',
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    touchAction: 'none',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <LogoView isClicked={isClicked} data={data} index={index} onClickDeleteButton={onClickDeleteButton} />
    </div>
  );
}

export default function MyStoreIntroductionBox(props) {
  const {
    isClicked,
    onClickModifyCenterInfo,

    data,
    storeLogo,
    setStoreLogo,
    setStoreLogoThumbnail,
    storeName,
    setStoreName,
    tel,
    setTel,
    type,
    setType,
    address,
    setAddress,
    storeIntro,
    setStoreIntro,

    profiles,
    profileThumbnails,
    setData
  } = props;

  const { t } = useLanguage();

  const LogoCanvasRef = useRef();
  const LogoHiddenFileInput = useRef(null);
  const CenterHiddenFileInput = useRef(null);

  const TopLevelRef = useRef(null);

  // 수정 모드에서 센터정보 영역 바깥 클릭 시 자동 저장 //
  useEffect(() => {
    if (!isClicked) return;

    function onClickOutside(e) {
      if (TopLevelRef.current && !TopLevelRef.current.contains(e.target)) onClickModifyCenterInfo();
    }

    document.addEventListener('mousedown', onClickOutside);

    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [isClicked, onClickModifyCenterInfo]);

  // 로고 변경 클릭 시 숨김 input 트리거 //
  function onClickLogoChange() {
    LogoHiddenFileInput.current.click();
  }

  // 로고 이미지 파일 처리 //
  function processLogoFile(file) {
    if (!file) return;

    const fileName = file.name;
    const pathpoint = fileName.lastIndexOf('.');
    const filepoint = fileName.substring(pathpoint + 1, fileName.length);
    const filetype = filepoint.toLowerCase();

    if (!isValidImageType(filetype)) {
      alert(t('myStoreSetting.imageTypeError'));
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      const base64 = reader.result;
      const naturalImage = new Image();

      naturalImage.src = reader.result.toString();

      naturalImage.onload = function (e) {
        const naturalWidth = e.target.naturalWidth;
        const naturalHeight = e.target.naturalHeight;

        const { resizeWidth, resizeHeight } = calculateResizedimensions(
          naturalWidth,
          naturalHeight,
          IMAGE_SIZE.LOGO
        );

        const image = new Image();
        const canvas = LogoCanvasRef.current;
        const ctx = canvas?.getContext('2d');

        if (!ctx) return;

        canvas.width = IMAGE_SIZE.LOGO;
        canvas.height = IMAGE_SIZE.LOGO;

        image.src = base64.toString();

        image.onload = function () {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(image, ((IMAGE_SIZE.LOGO - resizeWidth) / 2), ((IMAGE_SIZE.LOGO - resizeHeight) / 2), resizeWidth, resizeHeight);

          const dataUrl = canvas.toDataURL();

          setStoreLogo(dataUrl);
          setStoreLogoThumbnail(dataUrl);
        }
      }
    };

    reader.readAsDataURL(file);
  }

  function onChangeLogoFile(e) {
    if (e.target.files[0]) processLogoFile(e.target.files[0]);
  }

  function onDropLogoFile(file) {
    processLogoFile(file);
  }

  // 센터 사진 파일 처리 //
  function processCenterFile(file) {
    if (!file) return;

    const fileName = file.name;
    const pathpoint = fileName.lastIndexOf('.');
    const filepoint = fileName.substring(pathpoint + 1, fileName.length);
    const filetype = filepoint.toLowerCase();

    if (!isValidImageType(filetype)) {
      alert(t('myStoreSetting.imageTypeError'));
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      const base64 = reader.result;
      const naturalImage = new Image();

      naturalImage.src = reader.result.toString();

      if (!base64) return;

      naturalImage.onload = function (e) {
        const naturalWidth = e.target.naturalWidth;
        const naturalHeight = e.target.naturalHeight;

        const { resizeWidth, resizeHeight } = calculateResizedimensions(
          naturalWidth,
          naturalHeight,
          IMAGE_SIZE.CENTER
        );

        const image = new Image();
        // 각 파일마다 새로운 캔버스 생성 (공유 캔버스 race 방지) //
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) return;

        canvas.width = IMAGE_SIZE.CENTER;
        canvas.height = IMAGE_SIZE.CENTER;

        image.src = base64.toString();

        image.onload = function () {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(image, ((IMAGE_SIZE.CENTER - resizeWidth) / 2), ((IMAGE_SIZE.CENTER - resizeHeight) / 2), resizeWidth, resizeHeight);

          const profile = canvas.toDataURL();

          setData(prevData => {
            const currentProfiles = parseProfiles(prevData.profiles);
            const currentProfileThumbnails = parseProfiles(prevData.profileThumbnails);

            return {
              ...prevData,
              profiles: [...currentProfiles, profile],
              profileThumbnails: [...currentProfileThumbnails, profile]
            };
          });
        }
      }
    };

    reader.readAsDataURL(file);
  }

  function onClickAddPicture() {
    CenterHiddenFileInput.current.click();
  }

  function onChangeCenterFile(e) {
    if (!e.target.files || e.target.files.length === 0) return;

    const newFilesCount = e.target.files.length;
    const currentCount = profiles.length;

    if (currentCount + newFilesCount > MAX_CENTER_PHOTOS) {
      alert(t('myStoreSetting.maxCenterPhotos', { max: MAX_CENTER_PHOTOS }));
      return;
    }

    Array.from(e.target.files).forEach(file => processCenterFile(file));
  }

  function onDropCenterFiles(filesOrFile) {
    const files = Array.isArray(filesOrFile) ? filesOrFile : [filesOrFile];
    const newFilesCount = files.length;
    const currentCount = profiles.length;

    if (currentCount + newFilesCount > MAX_CENTER_PHOTOS) {
      alert(t('myStoreSetting.maxCenterPhotos', { max: MAX_CENTER_PHOTOS }));
      return;
    }

    files.forEach(file => processCenterFile(file));
  }

  // 센터 사진 Drag & Drop hook //
  const { isDragOver: isCenterPictureDragOver, dragHandlers: centerPictureDragHandlers } = useDragAndDrop({
    onFileSelect: onDropCenterFiles,
    enableDragDrop: true,
    allowMultiple: true,
    onInvalidFileType: () => alert(t('myStoreSetting.imageTypeError'))
  });

  // 순서 변경 센서 //
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 8 }
    })
  );

  // 드래그 종료 시 순서 변경 //
  function onDragEndPicture(event) {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const newProfiles = arrayMove(profiles, active.id, over.id);
    const newProfileThumbnails = arrayMove(profileThumbnails, active.id, over.id);

    setData({
      ...data,
      profiles: newProfiles,
      profileThumbnails: newProfileThumbnails
    });
  }

  function onClickDeletePicture(index) {
    const newProfiles = profiles.filter((_, i) => i !== index);
    const newProfileThumbnails = profileThumbnails.filter((_, i) => i !== index);

    setData({
      ...data,
      profiles: newProfiles,
      profileThumbnails: newProfileThumbnails
    });
  }

  // 좌측 카드 - 수정 모드 //
  function renderInfoEditMode() {
    return (
      <InfoCardContent>
        <InfoTopRow>
          <EditLogoWrapper>
            <EditLogoBox>
              <CenterLogoImage
                profile={storeLogo}
                defaultProfile={DefaultLogoPath}
                enableDragDrop={true}
                onFileSelect={onDropLogoFile} />
            </EditLogoBox>
            <PencilOverlay onClick={onClickLogoChange}>
              <PencilIcon />
            </PencilOverlay>
          </EditLogoWrapper>
          <canvas ref={LogoCanvasRef} style={{ display: 'none' }} />
          <input
            type="file"
            ref={LogoHiddenFileInput}
            onChange={onChangeLogoFile}
            accept="image/jpeg,image/png,image/jpg"
            style={{ display: 'none' }} />

          <InfoEditColumn>
            <LabeledDashedInput
              label={t('myStoreSetting.centerInfo.storeName')}
              defaultVal={storeName}
              setVal={setStoreName}
              maxLength={30}
              maxLengthAlert='myStoreSetting.centerInfo.storeNameMaxLengthAlert' />
            <LabeledDashedInput
              label={t('myStoreSetting.centerInfo.address')}
              defaultVal={address}
              setVal={setAddress}
              multiline={true}
              maxLength={100}
              maxLengthAlert='myStoreSetting.centerInfo.addressMaxLengthAlert' />
            <LabeledDashedInput
              label={t('myStoreSetting.centerInfo.phone')}
              defaultVal={tel}
              setVal={setTel}
              numericOnly={true} />
            <CenterTypeSelect
              label={t('myStoreSetting.centerInfo.type')}
              defaultVal={type}
              setVal={setType} />
          </InfoEditColumn>
        </InfoTopRow>
        <IntroSection>
          <IntroLabel>{t('myStoreSetting.centerInfo.centerIntro')}</IntroLabel>
          <CenterIntroTextArea
            placeholder={t('myStoreSetting.centerInfo.centerIntroPlaceholder')}
            defaultValue={storeIntro}
            setVal={setStoreIntro} />
        </IntroSection>
      </InfoCardContent>
    );
  }

  // 좌측 카드 - 조회 모드 //
  function renderInfoViewMode() {
    return (
      <InfoCardContent>
        <InfoTopRow>
          <ViewLogoBox>
            <UserProfile profile={storeLogo} defaultProfile={DefaultLogoPath} />
          </ViewLogoBox>
          <InfoTextColumn>
            <StoreNameText>{storeName}</StoreNameText>
            <InfoLineText>{address}</InfoLineText>
            <InfoLineText>{tel}</InfoLineText>
            <InfoLineText>{type}</InfoLineText>
          </InfoTextColumn>
        </InfoTopRow>
        <IntroSection>
          <IntroLabel>{t('myStoreSetting.centerInfo.centerIntro')}</IntroLabel>
          {
            storeIntro && storeIntro.trim() !== '' ?
              <IntroDisplayBox>
                <IntroText>{storeIntro}</IntroText>
              </IntroDisplayBox>
              :
              <IntroEmptyBox>
                <IntroEmptyText>{t('myStoreSetting.centerInfo.centerIntroEmpty')}</IntroEmptyText>
              </IntroEmptyBox>
          }
        </IntroSection>
      </InfoCardContent>
    );
  }

  // 우측 카드 - 사진 그리드 (수정 버튼과 무관하게 항상 편집 가능) //
  function renderPicturesGrid() {
    return (
      <ImageCardContent>
        <ImageHeader>
          <ImageHeaderTitle>{t('myStoreSetting.centerInfo.centerPhotos')}</ImageHeaderTitle>
          <ImageHeaderRight>
            {
              profiles.length > MAX_CENTER_PHOTOS &&
              <ImageHeaderWarning>{t('myStoreSetting.centerInfo.photosOverLimit', { max: MAX_CENTER_PHOTOS })}</ImageHeaderWarning>
            }
            <ImageHeaderCount>{t('myStoreSetting.centerInfo.centerPhotosCount', { current: profiles.length, max: MAX_CENTER_PHOTOS })}</ImageHeaderCount>
          </ImageHeaderRight>
        </ImageHeader>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEndPicture}>
          <SortableContext
            items={profiles.map((_, index) => index)}
            strategy={rectSortingStrategy}>
            <PictureGrid
              $isDragOver={isCenterPictureDragOver}
              {...centerPictureDragHandlers}>
              {
                profiles.map((picture, index) => (
                  <SortablePictureCell
                    key={'CenterPicture-' + index}
                    id={index}
                    isClicked={true}
                    data={picture}
                    index={index}
                    onClickDeleteButton={onClickDeletePicture} />
                ))
              }
              {
                profiles.length < MAX_CENTER_PHOTOS &&
                <AddBox onClick={onClickAddPicture}>
                  <AddIcon />
                </AddBox>
              }
            </PictureGrid>
          </SortableContext>
        </DndContext>
        <input
          type="file"
          ref={CenterHiddenFileInput}
          onChange={onChangeCenterFile}
          multiple
          accept="image/jpeg,image/png,image/jpg"
          style={{ display: 'none' }} />
      </ImageCardContent>
    );
  }

  return (
    <TopLevelWrapper ref={TopLevelRef}>
      <SectionTitleRow>
        <SectionTitleHighlight>{t('myStoreSetting.centerInfo.sectionLabel')}</SectionTitleHighlight>
        <SectionTitleText>{t('myStoreSetting.centerInfo.title')}</SectionTitleText>
      </SectionTitleRow>

      <CardsRow>
        <InfoCard>
          <CornerButton isEditing={isClicked} onClick={onClickModifyCenterInfo}>
            <CornerButtonText isEditing={isClicked}>
              {isClicked ? t('myStoreSetting.save') : t('myStoreSetting.modify')}
            </CornerButtonText>
          </CornerButton>
          {
            isClicked ?
              renderInfoEditMode()
              :
              renderInfoViewMode()
          }
        </InfoCard>

        <ImageCard>
          {renderPicturesGrid()}
        </ImageCard>
      </CardsRow>
    </TopLevelWrapper>
  )
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 15px;

  width: 100%;

  margin-top: 36px;
`;

const SectionTitleRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  height: 30px;
`;

const SectionTitleHighlight = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  color: ${props => props.theme.primary};

  margin: 0 0 0 0;
  white-space: nowrap;
`;

const SectionTitleText = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
  white-space: nowrap;
`;

const CardsRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: stretch;

  gap: 20px;

  width: 100%;
`;

const InfoCard = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  flex: 1 0 calc(50% - 10px); 
  min-width: 0;

  padding: 20px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};
`;

const CornerButton = styled.div`
  position: absolute;

  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  top: 0;
  right: 0;

  padding: 5px 10px;

  border-bottom-left-radius: 10px;
  border-top-right-radius: 10px;

  background-color: ${props => props.isEditing ? props.theme.primary : props.theme.buttonFill3};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const CornerButtonText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.isEditing ? props.theme.background : props.theme.white};

  margin: 0 0 0 0;
`;

const ImageCard = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  flex: 1 0 calc(50% - 10px); 
  min-width: 0;

  padding: 20px 10px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};
`;

const InfoCardContent = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  width: 100%;
`;

const InfoTopRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: stretch;

  gap: 20px;

  width: 100%;
`;

const ViewLogoBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex-shrink: 0;

  width: 148px;
  height: 148px;

  border-radius: 10px;

  overflow: hidden;
`;

const EditLogoWrapper = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-end;

  flex-shrink: 0;

  width: 148px;
  height: 148px;

  border-radius: 10px;
`;

const EditLogoBox = styled.div`
  position: absolute;

  inset: 0;

  width: 148px;
  height: 148px;

  border-radius: 10px;

  overflow: hidden;
`;

const PencilOverlay = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 33px;
  height: 33px;

  padding: 6px;
  box-sizing: border-box;

  border-top-right-radius: 13px;
  background-color: ${props => props.theme.blackAlpha10};

  & svg {
    width: 20px;
    height: 20px;
  }

  & svg path {
    fill: ${props => props.theme.white};
  }

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const InfoTextColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  flex: 1 0 0;
  min-width: 0;

  gap: 8px;
`;

const InfoEditColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  flex: 1 0 0;
  min-width: 0;

  gap: 10px;
`;

const StoreNameText = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const InfoLineText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const IntroSection = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 5px;

  width: 100%;
`;

const IntroLabel = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.subText};

  padding: 3px 0;
  margin: 0 0 0 0;
`;

const IntroDisplayBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  width: 100%;
  min-height: 146px;

  padding: 10px 15px;
  box-sizing: border-box;

  border: 1px solid ${props => props.theme.line};
  border-radius: 5px;
  background-color: ${props => props.theme.subBackground};
`;

const IntroText = styled.pre`
  font-family: NotoSansKR-Medium;
  font-size: 14px;
  line-height: 1.4;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;

  white-space: pre-wrap;
  word-break: break-all;
`;

const IntroEmptyBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 146px;

  padding: 10px 15px;
  box-sizing: border-box;

  border: 1px solid ${props => props.theme.line};
  border-radius: 5px;
  background-color: ${props => props.theme.subBackground};
`;

const IntroEmptyText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.bottomText};

  margin: 0 0 0 0;
  text-align: center;
`;

const ImageCardContent = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 15px;

  width: 100%;
`;

const ImageHeader = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;

  padding: 0 10px;
  box-sizing: border-box;
`;

const ImageHeaderTitle = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const ImageHeaderRight = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  gap: 8px;
`;

const ImageHeaderWarning = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 11px;

  color: ${props => props.theme.pointRed};

  margin: 0 0 0 0;
`;

const ImageHeaderCount = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const PictureGrid = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex-wrap: wrap;

  gap: 35px 10px;

  width: 100%;
  min-height: 143px;

  padding: 10px;
  box-sizing: border-box;

  border-radius: 5px;
  outline: 1px dashed ${props => props.$isDragOver ? props.theme.primary : 'transparent'};
  outline-offset: -1px;

  transition: outline-color 0.3s ease-in-out;
`;

const AddBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 123px;
  height: 123px;

  padding: 2px;
  box-sizing: border-box;

  border: 1px solid ${props => props.$isDragOver ? props.theme.primary : 'transparent'};
  border-radius: 5px;
  background-color: ${props => props.theme.buttonFill};

  transition: border-color 0.2s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      border-color: ${props => props.$isDragOver ? props.theme.primary : props.theme.primaryAlpha50};
    }
  }
`;

const AddIcon = styled(PlusIcon)`
  width: 60px;
  height: 60px;

  & path {
    fill: ${props => props.theme.line};
  }
`;
